import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Calculator, FlaskConical, TrendingUp, Download, Plus, Trash2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const Simulator = () => {
  const [activeTab, setActiveTab] = useState('hlb-calc');
  const [ingredients, setIngredients] = useState([]);
  const [emulsifiers, setEmulsifiers] = useState([]);
  
  // HLB Calculation State
  const [oilPhaseComponents, setOilPhaseComponents] = useState([]);
  const [hlbResult, setHlbResult] = useState(null);
  
  // Emulsifier Mix State
  const [emulsifierMixData, setEmulsifierMixData] = useState({
    target_hlb: 8.8,
    emulsifier_a_id: '',
    emulsifier_b_id: '',
    total_percentage: 4.5,
  });
  const [emulsifierMixResult, setEmulsifierMixResult] = useState(null);
  
  // Stability Evaluation State
  const [stabilityData, setStabilityData] = useState({
    emulsion_type: 'H/E',
    oil_phase: [],
    water_phase_percentage: 70,
    emulsifiers: [],
    other_ingredients: [],
  });
  const [stabilityResult, setStabilityResult] = useState(null);

  useEffect(() => {
    fetchIngredients();
  }, []);

  const fetchIngredients = async () => {
    try {
      const [oilsRes, emulsifiersRes] = await Promise.all([
        axios.get(`${API}/ingredients?type=oil`),
        axios.get(`${API}/ingredients?type=emulsifier`),
      ]);
      
      const buttersRes = await axios.get(`${API}/ingredients?type=butter`);
      
      setIngredients([...oilsRes.data, ...buttersRes.data]);
      setEmulsifiers(emulsifiersRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des ingrédients');
    }
  };

  // HLB Calculation Functions
  const addOilComponent = () => {
    setOilPhaseComponents([
      ...oilPhaseComponents,
      { ingredient_id: '', ingredient_name: '', percentage: 0 },
    ]);
  };

  const removeOilComponent = (index) => {
    setOilPhaseComponents(oilPhaseComponents.filter((_, i) => i !== index));
  };

  const updateOilComponent = (index, field, value) => {
    const updated = [...oilPhaseComponents];
    updated[index][field] = value;
    
    if (field === 'ingredient_id') {
      const ing = ingredients.find((i) => i.id === value);
      updated[index].ingredient_name = ing ? ing.name : '';
    }
    
    setOilPhaseComponents(updated);
  };

  const calculateHLB = async () => {
    const total = oilPhaseComponents.reduce((sum, comp) => sum + parseFloat(comp.percentage || 0), 0);
    
    if (Math.abs(total - 100) > 0.1) {
      toast.error('La somme des pourcentages doit être égale à 100%');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/simulator/calculate-hlb`, {
        components: oilPhaseComponents,
      });
      setHlbResult(response.data);
      toast.success('Calcul HLB réussi !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du calcul');
    }
  };

  // Emulsifier Mix Functions
  const calculateEmulsifierMix = async () => {
    if (!emulsifierMixData.emulsifier_a_id || !emulsifierMixData.emulsifier_b_id) {
      toast.error('Veuillez sélectionner deux émulsionnants');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/simulator/calculate-emulsifier-mix`, emulsifierMixData);
      setEmulsifierMixResult(response.data);
      toast.success('Calcul du mélange réussi !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du calcul');
    }
  };

  // Stability Evaluation Functions
  const addStabilityComponent = (phase) => {
    const newComponent = { ingredient_id: '', ingredient_name: '', percentage: 0 };
    
    setStabilityData({
      ...stabilityData,
      [phase]: [...stabilityData[phase], newComponent],
    });
  };

  const removeStabilityComponent = (phase, index) => {
    setStabilityData({
      ...stabilityData,
      [phase]: stabilityData[phase].filter((_, i) => i !== index),
    });
  };

  const updateStabilityComponent = (phase, index, field, value) => {
    const updated = [...stabilityData[phase]];
    updated[index][field] = value;
    
    if (field === 'ingredient_id') {
      const ingredientList = phase === 'emulsifiers' ? emulsifiers : ingredients;
      const ing = ingredientList.find((i) => i.id === value);
      updated[index].ingredient_name = ing ? ing.name : '';
    }
    
    setStabilityData({ ...stabilityData, [phase]: updated });
  };

  const evaluateStability = async () => {
    const oilTotal = stabilityData.oil_phase.reduce((sum, c) => sum + parseFloat(c.percentage || 0), 0);
    const emulsifierTotal = stabilityData.emulsifiers.reduce((sum, c) => sum + parseFloat(c.percentage || 0), 0);
    const otherTotal = stabilityData.other_ingredients.reduce((sum, c) => sum + parseFloat(c.percentage || 0), 0);
    const total = oilTotal + stabilityData.water_phase_percentage + emulsifierTotal + otherTotal;
    
    if (Math.abs(total - 100) > 0.1) {
      toast.error(`La somme totale doit être 100% (actuel: ${total.toFixed(1)}%)`);
      return;
    }
    
    try {
      const response = await axios.post(`${API}/simulator/evaluate-stability`, stabilityData);
      setStabilityResult(response.data);
      toast.success('Évaluation terminée !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'évaluation');
    }
  };

  const exportPDF = async () => {
    try {
      const response = await axios.post(`${API}/export/pdf`, stabilityData, {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'evaluation_stabilite.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Export PDF réussi !');
    } catch (error) {
      toast.error('Erreur lors de l\'export PDF');
    }
  };

  const exportExcel = async () => {
    try {
      const response = await axios.post(`${API}/export/excel`, stabilityData, {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'evaluation_stabilite.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Export Excel réussi !');
    } catch (error) {
      toast.error('Erreur lors de l\'export Excel');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-4xl font-semibold heading-font text-[#1A1C1B]" data-testid="simulator-title">
          Simulateur de Formulation
        </h2>
        <p className="text-[#5C615E] mt-2">
          Outils de calcul HLB et d'évaluation de stabilité
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="hlb-calc" data-testid="tab-hlb-calc">
            <Calculator className="h-4 w-4 mr-2" />
            Calcul HLB
          </TabsTrigger>
          <TabsTrigger value="emulsifier-mix" data-testid="tab-emulsifier-mix">
            <FlaskConical className="h-4 w-4 mr-2" />
            Mélange Émulsionnants
          </TabsTrigger>
          <TabsTrigger value="stability" data-testid="tab-stability">
            <TrendingUp className="h-4 w-4 mr-2" />
            Évaluation Stabilité
          </TabsTrigger>
        </TabsList>

        {/* HLB CALCULATION TAB */}
        <TabsContent value="hlb-calc" className="space-y-6">
          <Card className="border-[#E5E5DF]">
            <CardHeader>
              <CardTitle className="text-xl font-medium text-[#1A1C1B]">
                Calcul du HLB requis de la phase grasse
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                {oilPhaseComponents.map((comp, index) => (
                  <div key={index} className="flex gap-3 items-start">
                    <div className="flex-1">
                      <Label>Ingrédient</Label>
                      <Select
                        value={comp.ingredient_id}
                        onValueChange={(value) => updateOilComponent(index, 'ingredient_id', value)}
                      >
                        <SelectTrigger data-testid={`oil-ingredient-${index}`}>
                          <SelectValue placeholder="Sélectionner..." />
                        </SelectTrigger>
                        <SelectContent>
                          {ingredients.map((ing) => (
                            <SelectItem key={ing.id} value={ing.id}>
                              {ing.name} (HLB req: {ing.hlb_required || 'N/A'})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-32">
                      <Label>Pourcentage (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={comp.percentage}
                        onChange={(e) => updateOilComponent(index, 'percentage', parseFloat(e.target.value))}
                        className="border-[#E5E5DF]"
                        data-testid={`oil-percentage-${index}`}
                      />
                    </div>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => removeOilComponent(index)}
                      className="mt-6 border-[#E5E5DF] hover:bg-[#F6F7F6]"
                      data-testid={`remove-oil-${index}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={addOilComponent}
                  className="border-[#E5E5DF] hover:bg-[#F6F7F6]"
                  data-testid="add-oil-component"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Ajouter un composant
                </Button>
                <Button
                  onClick={calculateHLB}
                  disabled={oilPhaseComponents.length === 0}
                  className="bg-[#2F4840] hover:bg-[#22352F] text-white"
                  data-testid="calculate-hlb-button"
                >
                  <Calculator className="h-4 w-4 mr-2" />
                  Calculer HLB
                </Button>
              </div>

              {hlbResult && (
                <div className="mt-6 p-6 bg-[#E4EDE9] rounded-sm space-y-4" data-testid="hlb-result">
                  <div className="flex items-center justify-between">
                    <h3 className="text-2xl font-semibold heading-font text-[#2F4840]">
                      HLB Requis: {hlbResult.hlb_required}
                    </h3>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold uppercase tracking-wide text-[#5C615E] mb-2">
                      Détail des contributions
                    </h4>
                    <div className="space-y-2">
                      {hlbResult.breakdown.map((item, index) => (
                        <div
                          key={index}
                          className="flex justify-between items-center text-sm p-2 bg-white rounded-sm"
                        >
                          <span>{item.ingredient_name} ({item.percentage}%)</span>
                          <span className="font-medium mono-font">
                            HLB {item.hlb_required} × {item.percentage}% = {item.contribution}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold uppercase tracking-wide text-[#5C615E] mb-2">
                      Systèmes recommandés
                    </h4>
                    <div className="space-y-2">
                      {hlbResult.recommended_systems.map((system, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-2 text-sm p-2 bg-white rounded-sm"
                        >
                          <CheckCircle2 className="h-4 w-4 text-[#346A53]" />
                          <span>{system}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* EMULSIFIER MIX TAB */}
        <TabsContent value="emulsifier-mix" className="space-y-6">
          <Card className="border-[#E5E5DF]">
            <CardHeader>
              <CardTitle className="text-xl font-medium text-[#1A1C1B]">
                Calcul du mélange d'émulsionnants
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label>Émulsionnant A (lipophile)</Label>
                  <Select
                    value={emulsifierMixData.emulsifier_a_id}
                    onValueChange={(value) =>
                      setEmulsifierMixData({ ...emulsifierMixData, emulsifier_a_id: value })
                    }
                  >
                    <SelectTrigger data-testid="emulsifier-a-select">
                      <SelectValue placeholder="Sélectionner..." />
                    </SelectTrigger>
                    <SelectContent>
                      {emulsifiers.map((em) => (
                        <SelectItem key={em.id} value={em.id}>
                          {em.name} (HLB: {em.hlb_value})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Émulsionnant B (hydrophile)</Label>
                  <Select
                    value={emulsifierMixData.emulsifier_b_id}
                    onValueChange={(value) =>
                      setEmulsifierMixData({ ...emulsifierMixData, emulsifier_b_id: value })
                    }
                  >
                    <SelectTrigger data-testid="emulsifier-b-select">
                      <SelectValue placeholder="Sélectionner..." />
                    </SelectTrigger>
                    <SelectContent>
                      {emulsifiers.map((em) => (
                        <SelectItem key={em.id} value={em.id}>
                          {em.name} (HLB: {em.hlb_value})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>HLB Cible: {emulsifierMixData.target_hlb.toFixed(1)}</Label>
                <Slider
                  value={[emulsifierMixData.target_hlb]}
                  onValueChange={(value) =>
                    setEmulsifierMixData({ ...emulsifierMixData, target_hlb: value[0] })
                  }
                  min={3}
                  max={18}
                  step={0.1}
                  className="mt-2"
                  data-testid="target-hlb-slider"
                />
              </div>

              <div>
                <Label>Charge totale émulsionnant (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="15"
                  step="0.1"
                  value={emulsifierMixData.total_percentage}
                  onChange={(e) =>
                    setEmulsifierMixData({ ...emulsifierMixData, total_percentage: parseFloat(e.target.value) })
                  }
                  className="border-[#E5E5DF]"
                  data-testid="total-percentage-input"
                />
              </div>

              <Button
                onClick={calculateEmulsifierMix}
                className="bg-[#2F4840] hover:bg-[#22352F] text-white"
                data-testid="calculate-emulsifier-mix-button"
              >
                <Calculator className="h-4 w-4 mr-2" />
                Calculer le mélange
              </Button>

              {emulsifierMixResult && (
                <div className="mt-6 p-6 bg-[#E4EDE9] rounded-sm space-y-4" data-testid="emulsifier-mix-result">
                  <h3 className="text-xl font-semibold heading-font text-[#2F4840]">
                    Mélange pour HLB {emulsifierMixResult.target_hlb}
                  </h3>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-white rounded-sm">
                      <p className="text-sm text-[#5C615E] mb-1">{emulsifierMixResult.emulsifier_a.name}</p>
                      <p className="text-3xl font-semibold heading-font text-[#2F4840]">
                        {emulsifierMixResult.percentage_a}%
                      </p>
                      <p className="text-xs text-[#5C615E] mt-1 mono-font">
                        Fraction: {(emulsifierMixResult.fraction_a * 100).toFixed(1)}%
                      </p>
                    </div>

                    <div className="p-4 bg-white rounded-sm">
                      <p className="text-sm text-[#5C615E] mb-1">{emulsifierMixResult.emulsifier_b.name}</p>
                      <p className="text-3xl font-semibold heading-font text-[#2F4840]">
                        {emulsifierMixResult.percentage_b}%
                      </p>
                      <p className="text-xs text-[#5C615E] mt-1 mono-font">
                        Fraction: {(emulsifierMixResult.fraction_b * 100).toFixed(1)}%
                      </p>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold uppercase tracking-wide text-[#5C615E] mb-2">
                      Variations recommandées (±0.5 HLB)
                    </h4>
                    <div className="space-y-2">
                      {emulsifierMixResult.variations.map((variation, index) => (
                        <div
                          key={index}
                          className="flex justify-between items-center text-sm p-3 bg-white rounded-sm"
                        >
                          <span className="font-medium">HLB {variation.target_hlb}</span>
                          <div className="flex gap-4 mono-font text-xs">
                            <span>A: {variation.percentage_a}%</span>
                            <span>B: {variation.percentage_b}%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* STABILITY EVALUATION TAB */}
        <TabsContent value="stability" className="space-y-6">
          <Card className="border-[#E5E5DF]">
            <CardHeader>
              <CardTitle className="text-xl font-medium text-[#1A1C1B]">
                Évaluation de la stabilité de l'émulsion
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label>Type d'émulsion</Label>
                <Select
                  value={stabilityData.emulsion_type}
                  onValueChange={(value) => setStabilityData({ ...stabilityData, emulsion_type: value })}
                >
                  <SelectTrigger data-testid="emulsion-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="H/E">Huile dans Eau (H/E)</SelectItem>
                    <SelectItem value="E/H">Eau dans Huile (E/H)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label>Phase huileuse</Label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => addStabilityComponent('oil_phase')}
                    className="border-[#E5E5DF]"
                    data-testid="add-oil-phase"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Ajouter
                  </Button>
                </div>
                <div className="space-y-2">
                  {stabilityData.oil_phase.map((comp, index) => (
                    <div key={index} className="flex gap-2 items-start">
                      <Select
                        value={comp.ingredient_id}
                        onValueChange={(value) =>
                          updateStabilityComponent('oil_phase', index, 'ingredient_id', value)
                        }
                      >
                        <SelectTrigger className="flex-1" data-testid={`stability-oil-${index}`}>
                          <SelectValue placeholder="Ingrédient..." />
                        </SelectTrigger>
                        <SelectContent>
                          {ingredients.map((ing) => (
                            <SelectItem key={ing.id} value={ing.id}>
                              {ing.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={comp.percentage}
                        onChange={(e) =>
                          updateStabilityComponent('oil_phase', index, 'percentage', parseFloat(e.target.value))
                        }
                        className="w-24 border-[#E5E5DF]"
                        placeholder="%"
                        data-testid={`stability-oil-percentage-${index}`}
                      />
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => removeStabilityComponent('oil_phase', index)}
                        className="border-[#E5E5DF]"
                        data-testid={`remove-stability-oil-${index}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label>Phase aqueuse (%): {stabilityData.water_phase_percentage.toFixed(1)}</Label>
                <Slider
                  value={[stabilityData.water_phase_percentage]}
                  onValueChange={(value) =>
                    setStabilityData({ ...stabilityData, water_phase_percentage: value[0] })
                  }
                  min={0}
                  max={100}
                  step={0.5}
                  className="mt-2"
                  data-testid="water-phase-slider"
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label>Émulsionnants</Label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => addStabilityComponent('emulsifiers')}
                    className="border-[#E5E5DF]"
                    data-testid="add-emulsifier-stability"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Ajouter
                  </Button>
                </div>
                <div className="space-y-2">
                  {stabilityData.emulsifiers.map((comp, index) => (
                    <div key={index} className="flex gap-2 items-start">
                      <Select
                        value={comp.ingredient_id}
                        onValueChange={(value) =>
                          updateStabilityComponent('emulsifiers', index, 'ingredient_id', value)
                        }
                      >
                        <SelectTrigger className="flex-1" data-testid={`stability-emulsifier-${index}`}>
                          <SelectValue placeholder="Émulsionnant..." />
                        </SelectTrigger>
                        <SelectContent>
                          {emulsifiers.map((em) => (
                            <SelectItem key={em.id} value={em.id}>
                              {em.name} (HLB: {em.hlb_value})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        type="number"
                        min="0"
                        max="15"
                        step="0.1"
                        value={comp.percentage}
                        onChange={(e) =>
                          updateStabilityComponent('emulsifiers', index, 'percentage', parseFloat(e.target.value))
                        }
                        className="w-24 border-[#E5E5DF]"
                        placeholder="%"
                        data-testid={`stability-emulsifier-percentage-${index}`}
                      />
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => removeStabilityComponent('emulsifiers', index)}
                        className="border-[#E5E5DF]"
                        data-testid={`remove-stability-emulsifier-${index}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <Button
                onClick={evaluateStability}
                disabled={stabilityData.oil_phase.length === 0 || stabilityData.emulsifiers.length === 0}
                className="bg-[#2F4840] hover:bg-[#22352F] text-white"
                data-testid="evaluate-stability-button"
              >
                <TrendingUp className="h-4 w-4 mr-2" />
                Évaluer la stabilité
              </Button>

              {stabilityResult && (
                <div className="mt-6 space-y-4" data-testid="stability-result">
                  <div className="p-6 bg-gradient-to-br from-[#E4EDE9] to-[#FBFBF9] rounded-sm border border-[#E5E5DF]">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-3xl font-semibold heading-font text-[#2F4840]">
                          {stabilityResult.overall_score}%
                        </h3>
                        <p className="text-sm text-[#5C615E]">Score de stabilité global</p>
                      </div>
                      <div
                        className={`px-4 py-2 rounded-sm font-medium ${
                          stabilityResult.overall_score >= 85
                            ? 'bg-[#346A53]/10 text-[#346A53]'
                            : stabilityResult.overall_score >= 70
                            ? 'bg-[#2F4840]/10 text-[#2F4840]'
                            : 'bg-[#B94A3E]/10 text-[#B94A3E]'
                        }`}
                        data-testid="stability-level"
                      >
                        {stabilityResult.stability_level}
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={exportPDF}
                        className="border-[#E5E5DF]"
                        data-testid="export-pdf-button"
                      >
                        <Download className="h-3 w-3 mr-1" />
                        Export PDF
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={exportExcel}
                        className="border-[#E5E5DF]"
                        data-testid="export-excel-button"
                      >
                        <Download className="h-3 w-3 mr-1" />
                        Export Excel
                      </Button>
                    </div>
                  </div>

                  {stabilityResult.warnings.length > 0 && (
                    <div className="p-4 bg-[#B94A3E]/10 border border-[#B94A3E]/20 rounded-sm" data-testid="warnings-section">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="h-5 w-5 text-[#B94A3E] flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-[#B94A3E] mb-2">Avertissements</h4>
                          <ul className="space-y-1 text-sm text-[#1A1C1B]">
                            {stabilityResult.warnings.map((warning, index) => (
                              <li key={index}>• {warning}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="p-4 bg-[#346A53]/10 border border-[#346A53]/20 rounded-sm" data-testid="recommendations-section">
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-[#346A53] flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-[#346A53] mb-2">Recommandations</h4>
                        <ul className="space-y-1 text-sm text-[#1A1C1B]">
                          {stabilityResult.recommendations.map((rec, index) => (
                            <li key={index}>• {rec}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>

                  {stabilityResult.similar_formulas.length > 0 && (
                    <div className="p-4 bg-white border border-[#E5E5DF] rounded-sm">
                      <h4 className="text-sm font-semibold uppercase tracking-wide text-[#5C615E] mb-3">
                        Formules similaires stables ({stabilityResult.similar_formulas.length})
                      </h4>
                      <div className="space-y-2">
                        {stabilityResult.similar_formulas.map((formula, index) => (
                          <div key={index} className="text-sm p-2 bg-[#F6F7F6] rounded-sm">
                            <span className="font-medium">{formula.name}</span>
                            {formula.product_type && (
                              <span className="text-[#5C615E] ml-2">- {formula.product_type}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Simulator;
